import {
    UDViewPlaneFilterSettingModel as AiFilterModel,
    AiViewChangesResponse,
    ChatTurn
} from '../../../services/axios/axios-api-v2-generated';
import {
    UDViewDestinationTypeEnum,
    UDViewFlightFilterObjectEnum,
    UDViewPlaneFilterAirlineTypeEnum,
    UDViewPlaneFilterSettingModel,
    UDViewTagTextColor,
    UDViewTimeFilterTypeEnum
} from '../../../udView/udview';

export const AI_PROMPT_MAX_LENGTH = 2000;
export const VIEW_CHANGED_MESSAGE = 'The view changed while the AI was working, so nothing was applied. Please retry your request.';

const DEFAULT_AI_FILTER_COLOR = '#5585F5';
const MAX_HISTORY_TURNS = 10;

// Numeric values of the generated AiChatRole / AiResponseKind enums
const ROLE_USER = 0;
const ROLE_ASSISTANT = 1;
const RESPONSE_KINDS = ['changes', 'clarification', 'reply'] as const;

export type AiMessageKind = (typeof RESPONSE_KINDS)[number];

export interface AiClarification {
    question: string;
    suggestedAnswers: string[];
}

export interface AiAppliedChanges {
    added: UDViewPlaneFilterSettingModel[];
    updated: { alias: string; before: UDViewPlaneFilterSettingModel; after: UDViewPlaneFilterSettingModel }[];
    removed: { alias: string; index: number; filter: UDViewPlaneFilterSettingModel }[];
}

export interface AiChatMessage {
    id: number;
    role: 'user' | 'assistant';
    text: string;
    kind?: AiMessageKind;
    clarification?: AiClarification;
    changes?: AiAppliedChanges;
    reverted?: boolean;
}

export function parseResponseKind(kind: AiViewChangesResponse['kind']): AiMessageKind {
    return (kind !== undefined && RESPONSE_KINDS[kind]) || 'reply';
}

export function normalizeAiFilter(raw: AiFilterModel, index: number): UDViewPlaneFilterSettingModel {
    return {
        name: raw.name || `AI filter ${index + 1}`,
        color: raw.color || DEFAULT_AI_FILTER_COLOR,
        isActive: raw.isActive ?? true,
        include: raw.include ?? true,
        airlineType: (raw.airlineType as unknown as UDViewPlaneFilterAirlineTypeEnum) ?? UDViewPlaneFilterAirlineTypeEnum.SpecificAirlineId,
        useMyFleet: raw.useMyFleet ?? false,
        acid: raw.acid ?? '*',
        origin: raw.origin ?? '',
        destination: raw.destination ?? '',
        destinationType: (raw.destinationType as unknown as UDViewDestinationTypeEnum) ?? UDViewDestinationTypeEnum.OriginToDestination,
        useAltAirport: raw.useAltAirport ?? false,
        beaconCode: raw.beaconCode ?? '',
        icaoCode: raw.icaoCode ?? '',
        altitudeLower: raw.altitudeLower ?? null,
        altitudeUpper: raw.altitudeUpper ?? null,
        speedLower: raw.speedLower ?? null,
        speedUpper: raw.speedUpper ?? null,
        aircraftType: raw.aircraftType ?? '',
        filterObjectType: (raw.filterObjectType as unknown as UDViewFlightFilterObjectEnum) ?? UDViewFlightFilterObjectEnum.All,
        timeBefore: raw.timeBefore ?? null,
        timeAfter: raw.timeAfter ?? null,
        timeType: (raw.timeType as unknown as UDViewTimeFilterTypeEnum) ?? null,
        distanceToDestination: raw.distanceToDestination ?? null,
        sectorDeskId: raw.sectorDeskId ?? '',
        tail: raw.tail ?? '',
        diverted: raw.diverted ?? false,
        holding: raw.holding ?? false,
        showTags: raw.showTags ?? false,
        showHistory: raw.showHistory ?? false,
        showHolding: raw.holding ?? false,
        showDiverting: raw.diverted ?? false,
        tagTextColor: (raw.tagTextColor as unknown as UDViewTagTextColor) ?? UDViewTagTextColor.default
    };
}

function sameFilter(a: UDViewPlaneFilterSettingModel, b: UDViewPlaneFilterSettingModel) {
    return JSON.stringify(a) === JSON.stringify(b);
}

function sameFilters(a: UDViewPlaneFilterSettingModel[], b: UDViewPlaneFilterSettingModel[]) {
    return a.length === b.length && a.every((filter, i) => sameFilter(filter, b[i]));
}

export function resolveAliasIndex(alias: string, length: number): number | null {
    if (!/^F\d+$/.test(alias)) {
        return null;
    }
    const index = Number(alias.slice(1)) - 1;
    return index >= 0 && index < length ? index : null;
}

export type PlanResult = { ok: true; filters: UDViewPlaneFilterSettingModel[]; changes: AiAppliedChanges } | { ok: false };

export function planChanges(
    response: AiViewChangesResponse,
    snapshot: UDViewPlaneFilterSettingModel[],
    current: UDViewPlaneFilterSettingModel[]
): PlanResult {
    const updateAliases = (response.updateFilters ?? []).map((u) => u.alias ?? '');
    const removeAliases = response.removeFilterAliases ?? [];
    const counts = new Map<string, number>();
    [...updateAliases, ...removeAliases].forEach((alias) => counts.set(alias, (counts.get(alias) ?? 0) + 1));
    const usable = (alias: string) => counts.get(alias) === 1 && resolveAliasIndex(alias, snapshot.length) !== null;

    const updates = (response.updateFilters ?? []).filter((u) => u.filter && usable(u.alias ?? ''));
    const removals = removeAliases.filter(usable);

    if ((updates.length > 0 || removals.length > 0) && !sameFilters(snapshot, current)) {
        return { ok: false };
    }

    const updateByIndex = new Map(updates.map((u) => [resolveAliasIndex(u.alias!, snapshot.length)!, u]));
    const removeIndexes = new Set(removals.map((alias) => resolveAliasIndex(alias, snapshot.length)!));

    const changes: AiAppliedChanges = { added: [], updated: [], removed: [] };
    const filters: UDViewPlaneFilterSettingModel[] = [];
    current.forEach((filter, index) => {
        const update = updateByIndex.get(index);
        if (removeIndexes.has(index)) {
            changes.removed.push({ alias: `F${index + 1}`, index, filter });
        } else if (update) {
            const after = normalizeAiFilter(update.filter!, index);
            changes.updated.push({ alias: update.alias!, before: filter, after });
            filters.push(after);
        } else {
            filters.push(filter);
        }
    });
    (response.addFilters ?? []).forEach((raw, i) => {
        const added = normalizeAiFilter(raw, current.length + i);
        changes.added.push(added);
        filters.push(added);
    });
    return { ok: true, filters, changes };
}

export function hasChanges(changes: AiAppliedChanges) {
    return changes.added.length + changes.updated.length + changes.removed.length > 0;
}

export function revertChanges(current: UDViewPlaneFilterSettingModel[], changes: AiAppliedChanges): UDViewPlaneFilterSettingModel[] | null {
    const next = [...current];
    for (const { before, after } of changes.updated) {
        const i = next.findIndex((f) => sameFilter(f, after));
        if (i < 0) {
            return null;
        }
        next[i] = before;
    }
    for (const added of changes.added) {
        const i = next.findIndex((f) => sameFilter(f, added));
        if (i < 0) {
            return null;
        }
        next.splice(i, 1);
    }
    [...changes.removed]
        .sort((a, b) => a.index - b.index)
        .forEach(({ index, filter }) => next.splice(Math.min(index, next.length), 0, filter));
    return next;
}

export function buildChangeLines(changes: AiAppliedChanges): string[] {
    const name = (filter: UDViewPlaneFilterSettingModel, alias?: string) => filter.name || alias || 'unnamed filter';
    return [
        ...changes.added.map((f) => `Added filter "${name(f)}"`),
        ...changes.updated.map((u) => `Updated filter "${name(u.after, u.alias)}"`),
        ...changes.removed.map((r) => `Removed filter "${name(r.filter, r.alias)}"`)
    ];
}

export type AiChangeKind = 'added' | 'updated' | 'removed';

export interface AiChangeItem {
    kind: AiChangeKind;
    name: string;
    color?: string;
}

export function buildChangeItems(changes: AiAppliedChanges): AiChangeItem[] {
    const name = (filter: UDViewPlaneFilterSettingModel, alias?: string) => filter.name || alias || 'unnamed filter';
    return [
        ...changes.added.map((f): AiChangeItem => ({ kind: 'added', name: name(f), color: f.color })),
        ...changes.updated.map((u): AiChangeItem => ({ kind: 'updated', name: name(u.after, u.alias), color: u.after.color })),
        ...changes.removed.map((r): AiChangeItem => ({ kind: 'removed', name: name(r.filter, r.alias), color: r.filter.color }))
    ];
}

export function buildChangeSummary(changes: AiAppliedChanges): string {
    return buildChangeLines(changes).join('; ');
}

function buildAssistantContent(message: AiChatMessage): string {
    const parts = [message.text];
    if (message.clarification) {
        parts.push(`Options: ${message.clarification.suggestedAnswers.join(' / ')}`);
    }
    if (message.changes) {
        const summary = buildChangeSummary(message.changes);
        parts.push(message.reverted ? `${summary} (reverted by user)` : summary);
    }
    return parts.filter(Boolean).join('\n');
}

export function buildConversationHistory(messages: AiChatMessage[]): ChatTurn[] {
    return messages.slice(-MAX_HISTORY_TURNS).map((m) => ({
        role: m.role === 'user' ? ROLE_USER : ROLE_ASSISTANT,
        content: (m.role === 'user' ? m.text : buildAssistantContent(m)).slice(0, AI_PROMPT_MAX_LENGTH)
    }));
}
